import { Navigate, Route, Routes } from 'react-router';
import { useEffect } from 'react';
import { useCurrentUserQuery } from 'src/context/api/auth';
import Error404 from 'src/pages/error/404';
import { useDispatch } from 'react-redux';
import { useGetBrandsQuery } from 'src/context/api/competitorsMonitoring/api';
import { setNotificationBrandInNonBrandCampaigns } from 'src/context/reducers/filter-settings/filterSlice';
import { useGetBrandInNonBrandCampaignsQuery } from 'src/context/api/campaignExplorer/api';
import { paths } from './paths';
import { adminFeaturePaths } from './adminFeaturePaths';
import { dashboardPaths } from './dashboardPaths';

// --------------------------------------------------------------

// Brand > Search Performance
import SearchPerformanceOverview from 'src/pages/brand/search-performance/overview';
import SearchPerformanceFunnel from 'src/pages/brand/search-performance/funnel';
import SearchPerformanceExplore from 'src/pages/brand/search-performance/explore';
import SearchPerformanceClusterManager from 'src/pages/brand/search-performance/cluster-manager';
import SearchPerformanceConversionsAnalysis from 'src/pages/brand/search-performance/conversions-analysis';

// Brand > Visibility
import VisibilityOverview from 'src/pages/brand/visibility/overview';
import VisibilityCompetitionEvolution from 'src/pages/brand/visibility/competitors';
import VisibilitySearchTermsAnalysis from 'src/pages/brand/visibility/search-terms';
import VisibilityAds from 'src/pages/brand/visibility/ads';
import VisibilityLayout from 'src/layouts/visibility/visibility-layout';
import SearchPerformanceLayout from 'src/layouts/search-performance/search-performance-layout';

// Brand > Campaign Leaks
import CampaignLeaks from 'src/pages/brand/campaign-leaks';

// Brand > Brand Shield
import BrandShieldKeywordsExact from 'src/pages/brand/brand-shield/keywords-exact';
import BrandShieldKeywordsOther from 'src/pages/brand/brand-shield/keywords-other';
import BrandShieldCampaigns from 'src/pages/brand/brand-shield/campaigns';
import BrandShieldScraping from 'src/pages/brand/brand-shield/scraping';

// Old Activation
// import BrandShieldKoBiddingAuto from 'src/pages/brand/brand-shield/ko-bidding-auto';
// import BrandShieldKoBiddingManualMaxCpc from 'src/pages/brand/brand-shield/ko-bidding-manual/max-cpc';
// import BrandShieldKoBiddingManualTroas from 'src/pages/brand/brand-shield/ko-bidding-manual/troas';
// import BrandShieldKoBiddingManualTcpa from 'src/pages/brand/brand-shield/ko-bidding-manual/tcpa';
// import BrandShieldKoBiddingManualTargetImpressionShare from 'src/pages/brand/brand-shield/ko-bidding-manual/target-impression-share';
// import BrandShieldActiveMode from 'src/pages/brand/brand-shield/active-mode';
// import CampaignBased from 'src/sections/overview/dashboards/views/campaignBased';
// import AdGroupBased from 'src/sections/overview/dashboards/views/adGroupBased';

// Non Brand
import NonBrandedSearchPerformance from 'src/pages/non-branded/search-performance';
import NonBrandedVisibility from 'src/pages/non-branded/visibility';

// Competitors
import SuggestedBrands from 'src/pages/competitors/leaderboard/competitors-selection';
import Brands from 'src/pages/competitors/leaderboard/tracked-competitors';
import Leaderboard from 'src/pages/competitors/leaderboard/market-leaders';
import ConquestMode from 'src/pages/competitors/conquest-mode';

// Other / orphan
import BrandOverview from 'src/pages/dashboards/brandOverview';

// Admin / misc
import WorkspaceSetup from 'src/sections/workspaces/view/setup';
import AdminPage from 'src/pages/admin';
import Banners from 'src/sections/overview/dashboards/views/admin/onboard/views/banners';
import BannerAdd from 'src/sections/overview/dashboards/views/admin/onboard/views/add';
import AccountGeneral from 'src/pages/account';
import DataNotAvailable from 'src/pages/dashboards/dataNotAvailable';
import Section from 'src/components/section';
import Workspace from 'src/pages/workspaces';
import Join from 'src/pages/workspaces/join';
import Users from 'src/pages/workspaces/users';
import { useGetWorkspaceQuery } from 'src/context/api/workspaces';
import { SplashScreen } from 'src/components/loading-screen';
import { useSearchParams } from './hooks';

export default function Router() {
  const user: any = useCurrentUserQuery();
  const role = user?.data?.role;
  const isInternalRole = ['admin', 'brand specialist', 'consultant'].includes(role);
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const { data: workspace, isLoading: isLoadingWorkspace, isFetching: isFetchingWorkspace, isError: isErrorWorkspace } = useGetWorkspaceQuery({ id: parseInt(searchParams.get('workspace') as string) }, { skip: !searchParams.get('workspace') });

  const { data: brands, error: brandsError, isLoading: isLoadingBrands, isFetching: isFetchingBrands } = useGetBrandsQuery(
    { bqTable: workspace?.bqTable as string },
    { skip: !workspace?.bqTable }
  );

  const { data: brandInNonBrandCampaigns, error: brandInNonBrandCampaignsError, isLoading: isLoadingBrandInNonBrandCampaigns, isFetching: isFetchingBrandInNonBrandCampaigns } = useGetBrandInNonBrandCampaignsQuery(
    {
      client: workspace?.bqTable,
      brands: Array.isArray(brands) ? brands : [],
    },
    {
      skip: !workspace?.bqTable || !Array.isArray(brands) || brands.length === 0,
    }
  );

  useEffect(() => {
    if (brandInNonBrandCampaigns && brandInNonBrandCampaigns?.length > 0) {
      dispatch(setNotificationBrandInNonBrandCampaigns(true))
    }

  }, [brandInNonBrandCampaigns])

  if (isLoadingWorkspace || isFetchingWorkspace) {
    return <SplashScreen />
  }

  if (isErrorWorkspace) {
    return <Navigate to="/brand-overview" replace />
  }

  return (
    <Routes>
      {/*  */}
      <Route
        path="/"
        element={
          ['admin', 'brand specialist', 'consultant'].includes(role)
            ? <Navigate to="/admin/cockpit" replace />
            : <Section children={<Workspace />} />
        }
      />

      {/*  */}
      {/* <Route path={dashboardPaths.competitorsMonitoring} element={<Section children={<CompetitorsMonitoring />} />} /> */}
      {/* <Route path={dashboardPaths.competitorFocus} element={<Section children={<CompetitorFocus />} />} /> */}
      {/* <Route path={dashboardPaths.textAds} element={<Section children={<GlobalTextAds />} />} /> */}
      {/* <Route path={dashboardPaths.productListingAds} element={<Section children={<GlobalPlaAds />} />} /> */}
      {/* <Route path={dashboardPaths.competitorDomain} element={<Section children={<Competitor />} />} /> */}
      {/*  */}

      {/* Search Performance section: overview/funnel/explore/cluster-manager share a parent
          layout so the date range persists across them once overridden. Cluster Manager keeps
          its role gate as a conditional child. */}
      <Route path="/brand/search-performance" element={<Section children={<SearchPerformanceLayout />} />}>
        <Route path="overview" element={<SearchPerformanceOverview />} />
        <Route path="funnel" element={<SearchPerformanceFunnel />} />
        <Route path="explore" element={<SearchPerformanceExplore />} />
        <Route path="cluster-manager" element={<SearchPerformanceClusterManager />} />
      </Route>
      <Route path={dashboardPaths.brandOverview} element={<Section children={<BrandOverview />} />} />

      <Route path={dashboardPaths.searchPerformanceConversionsAnalysis} element={<Section children={<SearchPerformanceConversionsAnalysis />} />} />


      <Route path={dashboardPaths.campaignLeaks} element={<Section children={<CampaignLeaks />} />} />

      {(isInternalRole || workspace?.nonbrand_visibility?.includes(role)) && <Route path={dashboardPaths.nonBrandSearchPerformance} element={<Section children={<NonBrandedSearchPerformance />} />} />}
      {/* Les pages visibility sont montées via VisibilityLayout : c'est lui qui pose la plage
          de dates par défaut et conserve la sélection de l'utilisateur d'une sous-page à
          l'autre. Elles ne doivent donc exister qu'ici — une route déclarée ailleurs pour le
          même chemin prendrait le dessus et le layout ne monterait plus. */}
      <Route path="/brand/visibility" element={<Section children={<VisibilityLayout />} />}>
        <Route path="overview" element={<VisibilityOverview />} />
        <Route path="competitors" element={<VisibilityCompetitionEvolution />} />
        <Route path="search-terms" element={<VisibilitySearchTermsAnalysis />} />
        <Route path="ads" element={<VisibilityAds />} />
      </Route>
      <Route path={dashboardPaths.nonBrandLegacyRoot} element={<Section children={<NonBrandedSearchPerformance />} />} />

      {workspace?.brandpulse_visibility?.includes(role) && <Route path={dashboardPaths.leaderboardTrackedCompetitors} element={<Section children={<Brands />} />} />}
      {workspace?.brandpulse_visibility?.includes(role) && <Route path={dashboardPaths.leaderboardCompetitorsSelection} element={<Section children={<SuggestedBrands />} />} />}
      {workspace?.brandpulse_visibility?.includes(role) && <Route path={dashboardPaths.leaderboardMarketLeaders} element={<Section children={<Leaderboard />} />} />}


      {workspace?.id && (isInternalRole || workspace?.nonbrand_visibility?.includes(role)) && (
        <>
          <Route path={dashboardPaths.nonBrandSearchPerformance} element={<Section children={<NonBrandedSearchPerformance />} />} />
          <Route path={dashboardPaths.nonBrandVisibility} element={<Section children={<NonBrandedVisibility />} />} />
        </>
      )}

      {workspace?.id && workspace?.conquestmode && workspace?.conquestmode_visibility?.includes(role) && (
        <>
          <Route path={dashboardPaths.conquestMode} element={<Section children={<ConquestMode />} />} />
        </>
      )}

      {["admin", "brand specialist", "consultant"].includes(role) && (
        <>
          <Route path="/admin" element={<Navigate to="/admin/cockpit" replace />} />
          <Route path="/admin/cockpit" element={<Section children={<AdminPage tab="cockpit" />} />} />
          <Route path="/admin/users" element={<Section children={<AdminPage tab="users" />} />} />
          <Route path="/admin/users/add" element={<Section children={<AdminPage tab="users" />} />} />
        </>
      )}

      {role === "admin" && (
        <>
          <Route path="/admin/sso" element={<Section children={<AdminPage tab="sso" />} />} />
          <Route path={paths.admin.sso.list} element={<Navigate to="/admin/sso" replace />} />
          <Route path={paths.admin.onboard.banners.list} element={<Section children={<Banners />} />} />
          <Route path={paths.admin.onboard.banners.add} element={<Section children={<BannerAdd />} />} />
        </>
      )}

      {["admin", "brand specialist", "consultant"].includes(role) && (
        <>
          <Route path={paths.admin.workspacesSetup} element={<Section children={<WorkspaceSetup />} />} />
          <Route path={paths.admin.workspacesSetupEdit} element={<Section sx={{ height: '100vh', pb: 0 }} children={<WorkspaceSetup />} />} />
        </>
      )}


      {["admin", "brand specialist"].includes(role) && (
        <>
          {/* Brand Shield */}
          <Route path={adminFeaturePaths.activationsKeywordsExact} element={<Section children={<BrandShieldKeywordsExact />} />} />
          <Route path={adminFeaturePaths.activationsKeywordsOther} element={<Section children={<BrandShieldKeywordsOther />} />} />
          <Route path={adminFeaturePaths.activationsCampaigns} element={<Section children={<BrandShieldCampaigns />} />} />
          <Route path={adminFeaturePaths.liveMonitoring} element={<Section children={<BrandShieldScraping />} />} />

          {/* 
          <Route path={adminFeaturePaths.liveMonitoringAdd} element={<Section children={<BrandShieldScraping />} />} />
          <Route path={adminFeaturePaths.liveBiddingSearchActiveMode} element={<Section children={<BrandShieldActiveMode />} />} />
          <Route path={adminFeaturePaths.liveBiddingSearchKoBiddingMaxCpc} element={<Section children={<BrandShieldKoBiddingManualMaxCpc />} />} />
          <Route path={adminFeaturePaths.liveBiddingSearchKoBiddingTroas} element={<Section children={<BrandShieldKoBiddingManualTroas />} />} />
          <Route path={adminFeaturePaths.liveBiddingSearchKoBiddingTcpa} element={<Section children={<BrandShieldKoBiddingManualTcpa />} />} />
          <Route path={adminFeaturePaths.liveBiddingSearchKoBiddingTargetImpressionShare} element={<Section children={<BrandShieldKoBiddingManualTargetImpressionShare />} />} />
          <Route path={adminFeaturePaths.liveBiddingSearchKoBiddingAuto} element={<Section children={<BrandShieldKoBiddingAuto />} />} />
          <Route path={adminFeaturePaths.liveBiddingCampaignBased} element={<Section children={<CampaignBased />} />} />
          <Route path={adminFeaturePaths.liveBiddingAdGroupBased} element={<Section children={<AdGroupBased />} />} /> 
          */}
        </>
      )}

      <Route path="/account" element={<Section children={<AccountGeneral />} />} />
      <Route path='/data-not-available' element={<Section children={<DataNotAvailable />} />} />
      <Route
        path='/workspaces'
        element={
          ['admin', 'brand specialist', 'consultant'].includes(role)
            ? <Navigate to="/admin/cockpit" replace />
            : <Section children={<Workspace />} />
        }
      />
      <Route
        path='/workspaces/add'
        element={<Section children={<Workspace />} />}
      />
      {/*  */}
      <Route path='/workspaces/:id/invitations/:token' element={<Section children={<Join />} />} />
      <Route path='/workspaces/:id/invitations/:token' element={<Section children={<Join />} />} />
      <Route path='/workspaces/:id' element={<Section children={<Users />} />} />
      {/*  */}


      <Route path="/404" element={<Section children={<Error404 />} />} />

      {/* Wildcard catch-all */}
      <Route
        path="*"
        element={
          <>
            <Navigate
              to="/404"
              replace
            />
          </>
        }
      />
    </Routes>
  );
}